import { Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaExclamationTriangle, FaFlag, FaPlus } from "react-icons/fa";
import { DropdownSelect, IOptions } from "components/DropdownSelect";
import { CustomInput } from "components/CustomInput";
import { Required } from "components/Required";
import { SwitchCustom } from "components/Switch";
import FileUpload from "components/UploadArquivos";
import { formatarDinheiro } from "_utils/Money";
import { useMaskInput } from "hooks/useMaskInput";
import { criarLoteGPSchema } from "../../../validate/criarLoteGPSchema";
import { IFormCriarLote } from "../index";
import {
  AlertaValorIcone,
  AvisoSemRecibo,
  Button,
  Container,
  ContainerForm,
  ContainerHeader,
  ContainerInstrucoes,
  HeaderButtonsContainer,
  PagamentoUrgenteContainer,
  Title,
  ValorInputWrapper,
} from "./style";

interface IProps {
  optionsBeneficiarios: IOptions[];
  formData: IFormCriarLote;
  setFormData: React.Dispatch<React.SetStateAction<IFormCriarLote>>;
  onCancelar: () => void;
  onSelecionarProcessos: () => void;
  onSalvarLote: () => void;
  disabledSalvar?: boolean;
  valorDivergente?: boolean;
  bloquearBeneficiario?: boolean;
  titulo?: string;
  textoCancelar?: string;
  textoSalvar?: string;
  avisoSemRecibo?: boolean;
}

export function FormCriarLote({
  optionsBeneficiarios,
  formData,
  setFormData,
  onCancelar,
  onSelecionarProcessos,
  onSalvarLote,
  disabledSalvar,
  valorDivergente,
  bloquearBeneficiario = false,
  titulo = "Criar novo lote de pagamento",
  textoCancelar = "Cancelar",
  textoSalvar = "Salvar lote",
  avisoSemRecibo = false,
}: IProps) {
  const { mInputValorBR } = useMaskInput();

  const {
    control,
    trigger,
    setValue,
    formState: { errors },
  } = useForm<{ idBeneficiario: number; numeroNfRecibo: string; valorNfRecibo: string }>({
    resolver: yupResolver(criarLoteGPSchema),
    defaultValues: {
      idBeneficiario: formData.idBeneficiario >= 0 ? formData.idBeneficiario : undefined,
      numeroNfRecibo: formData.numeroNfRecibo,
      valorNfRecibo: formData.valorNfRecibo,
    },
  });

  const handleSalvar = async () => {
    const valido = await trigger();
    if (!valido) return;
    onSalvarLote();
  };

  return (
    <Container>
      <ContainerHeader>
        <Title>{titulo}</Title>
        <HeaderButtonsContainer>
          <Button outline variante="black" onClick={onCancelar}>
            {textoCancelar}
          </Button>
          <Button
            variante="black"
            disabled={formData.idBeneficiario < 0}
            onClick={onSelecionarProcessos}
          >
            <FaPlus /> Selecionar processos
          </Button>
          <Button variante="black" disabled={disabledSalvar} onClick={handleSalvar}>
            {textoSalvar}
          </Button>
        </HeaderButtonsContainer>
      </ContainerHeader>

      <hr />

      {avisoSemRecibo && (
        <AvisoSemRecibo>
          <FaExclamationTriangle />
          Lote sem Recibo / NF vinculado, clique no botão ao lado para fazer upload do arquivo.
        </AvisoSemRecibo>
      )}

      <ContainerInstrucoes>
        Preencha os campos abaixo com os dados do Recibo ou NF e clique no botão acima para
        selecionar os processos que deseja adicionar custo.
      </ContainerInstrucoes>

      <ContainerForm>
        <Row>
          <Col style={{ minWidth: 0 }}>
            <Form.Group>
              <Form.Label>Tipo de lançamento</Form.Label>
              <DropdownSelect
                options={[{ value: "C", label: "CUSTOS DO PROCESSO" }]}
                selectedValue="C"
                onSelect={() => {}}
                disabled
                exibirValorDesabilitado
              />
            </Form.Group>
          </Col>

          <Col style={{ minWidth: 0 }}>
            <Form.Group>
              <Form.Label>
                Beneficiário <Required />
              </Form.Label>
              <Controller
                control={control}
                name="idBeneficiario"
                render={({ field: { onChange } }) => {
                  const dropdown = (
                    <DropdownSelect
                      options={optionsBeneficiarios}
                      selectedValue={formData.idBeneficiario >= 0 ? formData.idBeneficiario : undefined}
                      onSelect={(v) => {
                        onChange(v);
                        setFormData((prev) => ({ ...prev, idBeneficiario: Number(v) }));
                      }}
                      searchable
                      permitirZero
                      disabled={bloquearBeneficiario}
                      exibirValorDesabilitado
                      mensagemErro={errors.idBeneficiario?.message}
                    />
                  );

                  if (!bloquearBeneficiario) return dropdown;

                  return (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          Remova os processos do lote para alterar o beneficiário.
                        </Tooltip>
                      }
                    >
                      <div>{dropdown}</div>
                    </OverlayTrigger>
                  );
                }}
              />
            </Form.Group>
          </Col>

          <Col style={{ minWidth: 0 }}>
            <Form.Group>
              <Form.Label>
                Nº Recibo / NF <Required />
              </Form.Label>
              <Controller
                control={control}
                name="numeroNfRecibo"
                render={({ field: { onChange, value } }) => (
                  <CustomInput
                    type="text"
                    placeholder="Recibo/NF"
                    style={{ height: "2.2rem" }}
                    value={value ?? ""}
                    onChange={(e: any) => {
                      onChange(e);
                      setFormData((prev) => ({ ...prev, numeroNfRecibo: e.target.value }));
                    }}
                    mensagemErro={errors.numeroNfRecibo?.message}
                    isInvalid={!!errors.numeroNfRecibo}
                  />
                )}
              />
            </Form.Group>
          </Col>

          <Col style={{ minWidth: 0 }}>
            <Form.Group>
              <Form.Label>
                Valor total do Recibo / NF <Required />
              </Form.Label>
              <Controller
                control={control}
                name="valorNfRecibo"
                render={({ field: { onChange, value } }) => (
                  <ValorInputWrapper>
                    <CustomInput
                      type="text"
                      placeholder="R$ 0,00"
                      style={{
                        height: "2.2rem",
                        ...(valorDivergente
                          ? { borderColor: "#fe8f14", paddingRight: "2rem" }
                          : {}),
                      }}
                      value={
                        value != null && value !== ""
                          ? formatarDinheiro(Number(value))
                          : ""
                      }
                      onChange={(e: any) => {
                        const raw = mInputValorBR(e);
                        onChange(raw);
                        setFormData((prev) => ({ ...prev, valorNfRecibo: raw }));
                      }}
                      mensagemErro={errors.valorNfRecibo?.message}
                      isInvalid={!!errors.valorNfRecibo}
                    />
                    {valorDivergente && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Valor divergente da soma dos custos adicionados
                          </Tooltip>
                        }
                      >
                        <AlertaValorIcone>
                          <FaExclamationTriangle />
                        </AlertaValorIcone>
                      </OverlayTrigger>
                    )}
                  </ValorInputWrapper>
                )}
              />
            </Form.Group>
          </Col>

          <Col style={{ minWidth: 0 }}>
            <Form.Group>
              <Form.Label>Upload do Recibo / NF</Form.Label>
              <FileUpload
                id="uploadRecibo"
                onFileUpload={(file) =>
                  setFormData((prev) => ({ ...prev, arquivoRecibo: file }))
                }
                inputAccept=""
                fileInicial={formData.arquivoRecibo}
              />
            </Form.Group>
          </Col>

        </Row>

        <Row style={{ marginTop: "0.75rem" }}>
          <Col style={{ flex: "0 0 20%", maxWidth: "20%", minWidth: 0 }}>
            <Form.Group>
              <Form.Label>Upload boleto</Form.Label>
              <FileUpload
                id="uploadBoleto"
                multiple
                limparAoSelecionar
                onLimpar={() =>
                  setFormData((prev) => ({ ...prev, arquivosBoleto: [] }))
                }
                textoSelecionado={
                  formData.arquivosBoleto.length === 1
                    ? formData.arquivosBoleto[0].name
                    : formData.arquivosBoleto.length > 1
                      ? `${formData.arquivosBoleto.length} selecionados`
                      : undefined
                }
                onFileUpload={(file) =>
                  setFormData((prev) => ({
                    ...prev,
                    arquivosBoleto: [...prev.arquivosBoleto, file],
                  }))
                }
                onFilesUpload={(files) =>
                  setFormData((prev) => ({
                    ...prev,
                    arquivosBoleto: [...prev.arquivosBoleto, ...files],
                  }))
                }
                inputAccept=""
              />
            </Form.Group>
          </Col>

          <Col style={{ flex: "0 0 60%", maxWidth: "60%" }}>
            <Form.Group>
              <Form.Label>Observação</Form.Label>
              <CustomInput
                rows={1}
                placeholder="Informações adicionais"
                style={{ resize: "none" }}
                value={formData.observacao}
                onChange={(e: any) =>
                  setFormData((prev) => ({ ...prev, observacao: e.target.value }))
                }
              />
            </Form.Group>
          </Col>

          <Col style={{ flex: "0 0 20%", maxWidth: "20%" }}>
            <PagamentoUrgenteContainer>
              <SwitchCustom
                checked={formData.pagamentoUrgente}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, pagamentoUrgente: e.target.checked }))
                }
                title="Pagamento urgente"
                corProjeto
              />
              <span>Pagamento urgente</span>
              <FaFlag style={{ color: "#ee5353" }} />
            </PagamentoUrgenteContainer>
          </Col>
        </Row>
      </ContainerForm>
    </Container>
  );
}
